// service/task/hooks/useGetAllTasksQuery.ts
import { useQuery, useInfiniteQuery, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { getAllTasks, GetAllTasksPayload } from "../api/getAllTasks";
import { GetTasksSuccessResponse } from "../../../types/project/tasks";
import { ApiError } from "../../../types/apiError";

export const useGetAllTasksQuery = (payload: GetAllTasksPayload) =>
  useQuery<GetTasksSuccessResponse, ApiError>({
    queryKey: ["tasks", payload.projectUuid, JSON.stringify(payload)],
    queryFn: () => getAllTasks(payload),
    enabled: Boolean(payload.projectUuid),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

export const useGetInfiniteTasksQuery = (payload: GetAllTasksPayload) => {
  const queryClient = useQueryClient();
  const queryKey = ["tasks", "infinite", payload.projectUuid, JSON.stringify(payload)];

  return useInfiniteQuery<any, ApiError>({
    queryKey,
    queryFn: async ({ pageParam = null }) => {
      const payloadToSend = { ...payload, cursor: pageParam as string | null };
      try {
        const response = await getAllTasks(payloadToSend);
        return response;
      } catch (error: any) {
        if (error?.status === 400 && pageParam !== null) {
          queryClient.resetQueries({ queryKey });
        }
        throw error;
      }
    },
    getNextPageParam: (lastPage: any) => {
      const pageData = lastPage.response?.[0] || lastPage.response || lastPage;
      const nextCursor = pageData?.nextCursor;
      const hasMore = pageData?.hasMore;
      
      const result = hasMore && nextCursor ? nextCursor : undefined;
      return result;
    },
    initialPageParam: null as string | null,
    enabled: Boolean(payload.projectUuid) && !payload.skipFetch,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    retry: false,
  });
};
